import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect, useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import BookingSummaryItem from '../BookingSummaryItem';
import ErrorToast from '../../../design-system/toast/errorToast/ErrorToast';
import OneSessionToggle from '../OneSessionToggle';
import NewBookingAPI from '../../../../services/new-booking';

import {
  closeBookingModal,
  cleanErrorState,
  createAppointmentAndRedirectToAnotherPage,
  createAppointmentAndGetUpdatedStudentsBooking,
  addToBookingSelectedTimestamp,
  removeFromBookingSelectedTimestamp,
  setBulkingState,
  setShowOptions,
  setSelectedAction,
  NewBookingActionTypes
} from '../../../../actions/new-booking';
import { getSessionsStatus } from '../../../../actions/sessions';
import { actionCreatorsFactory } from '../../../../utils/action-helpers';
import { sendSentryError } from '../../../../setup/sentry';

import {
  cleanSelectedTimestamp,
  preBookDate,
  preBookWholeMonthDate,
  removeFromPreBook,
  removeMonthFromPreBook,
  storeMonthSessionsAvailability,
  setCurrentDate
} from '../../../../actions/bookable';
import SessionsManagementModal from '../SessionsManagementModal';
import { selectProductType } from '../../../../reducers/order-info';
import { getDateFromIsoString, getTimeFromIsoString } from '../../../../utils/time-helpers';
import { calculateBookings } from './domain/calculateBookings';
import SessionsAlert from '../sessions-alert/sessions-alert';
import { buildMonthAndYearStr } from '../../../../reducers/bookable';
import { getMonthTranslated } from '../../../design-system/calendar/TimeRelated.enum';
import { CLASS_TYPES } from '../../Enum';
import mixpanelHelper from '../../../../utils/mixpanel-helper';
import useInitRecording from '../../../../hooks/useInitRecording';
import { autoFillSchedule } from '../../utils/auto-schedule';
import { selectBookedDates } from '../../../../reducers/sessions';

const BookingTuitionSessionModal = ({
  addToBookingSelectedTimestampAction,
  availableSessionsThisMonth,
  availableDatetimesMap,
  calendar,
  cleanErrorStateAction,
  daysAvailableThisMonth,
  cleanSelectedTimestampAction,
  setCurrentDateAction,
  createAppointmentAndGetUpdatedStudentsBookingAction,
  createAppointmentAndRedirectToAnotherPageAction,
  currentLang,
  errorMessage,
  hasErrorOnBooking,
  isLoadingFutureScheduleAvailability,
  isSchedulingAppointment,
  matchedTutor,
  pendingToBeScheduled,
  preBookDateAction,
  preBookWholeMonthDateAction,
  productType,
  redirectTo,
  removeFromPreBookAction,
  removeFromBookingSelectedTimestampAction,
  selectedCourse,
  selectedTutor,
  selectedTimestamp,
  sessionsPerMonth,
  storeMonthSessionsAvailabilityAction,
  closeBookingModalAction,
  removeMonthFromPreBookAction,
  student,
  timezone
}) => {
  const isBulkBookingEnabled = useFeatureIsOn('bulk-bookings');
  const dispatch = useDispatch();

  const [activeAppointmentIndex, setActiveAppointmentIndex] = useState(0);
  const [newSchedule, setNewSchedule] = useState([]);
  const [autoFillEnabled, setAutoFillEnabled] = useState(isBulkBookingEnabled); // Only auto-fill if feature is enabled
  const [alternativeSelections, setAlternativeSelections] = useState(new Set());
  const [endRecordingWholePage] = useInitRecording((timeSpent) =>
    mixpanelHelper.lessonAction('Booked', {
      timeSpent,
      type: 'tuition'
    })
  );

  // Create action creators for managing loading state
  const createAppointmentAndGetUpdatedStudentsBookingActions = actionCreatorsFactory(
    [
      NewBookingActionTypes.CREATING_NEW_APPOINTMENT,
      NewBookingActionTypes.CREATING_NEW_APPOINTMENT_SUCCESS,
      NewBookingActionTypes.CREATING_NEW_APPOINTMENT_FAIL
    ],
    'createAppointmentAndUpdatedBooking'
  );

  // Helper function to extract error messages
  const extractErrorMessage = (error, defaultMessage) => {
    if (!error.response) return defaultMessage;
    if (error.response.data?.detail) return error.response.data.detail;
    if (error.response.data?.message) return error.response.data.message;
    return defaultMessage;
  };

  // Get existing booked timestamps to account for existing sessions
  const bookedTimestamps = useSelector((state) => selectBookedDates(state));

  const history = useHistory();

  // DEBUG: Track when pendingToBeScheduled changes
  useEffect(() => {
    console.log(
      '🔍 DEBUGGING - pendingToBeScheduled changed:',
      pendingToBeScheduled.map((l) => ({ id: l.id, number: l.number, date: l.newDateAndTime || l.appointment_object?.date || 'NO DATE' }))
    );
  }, [pendingToBeScheduled]);

  // DEBUG: Track when newSchedule changes
  useEffect(() => {
    console.log(
      '🔍 DEBUGGING - newSchedule changed:',
      newSchedule.map((l) => ({ id: l.id, number: l.number, date: l.newDateAndTime || 'NO DATE' }))
    );
  }, [newSchedule]);

  // Create a merged schedule that combines pendingToBeScheduled with reordered newSchedule data
  const displaySchedule = useMemo(() => {
    if (newSchedule.length === 0) {
      return pendingToBeScheduled;
    }

    // Create a map of reordered lessons from newSchedule for quick lookup
    const newScheduleMap = new Map();
    newSchedule.forEach((lesson) => {
      newScheduleMap.set(lesson.id, lesson);
    });

    // Separate scheduled and unscheduled lessons
    const scheduledLessons = [];
    const unscheduledLessons = [];

    pendingToBeScheduled.forEach((originalLesson) => {
      const reorderedLesson = newScheduleMap.get(originalLesson.id);
      if (reorderedLesson) {
        // This lesson is scheduled - use reordered data
        scheduledLessons.push({
          ...originalLesson,
          number: reorderedLesson.number,
          newDateAndTime: reorderedLesson.newDateAndTime
        });
      } else {
        // This lesson is not scheduled yet - keep original data
        unscheduledLessons.push(originalLesson);
      }
    });

    // Sort scheduled lessons by lesson number
    const sortedScheduledLessons = scheduledLessons.sort((a, b) => a.number - b.number);

    // For unscheduled lessons, assign sequential numbers starting after the highest scheduled lesson number
    const highestScheduledNumber = sortedScheduledLessons.length > 0 ? Math.max(...sortedScheduledLessons.map((l) => l.number)) : 0;

    const numberedUnscheduledLessons = unscheduledLessons.map((lesson, index) => ({
      ...lesson,
      number: highestScheduledNumber + 1 + index
    }));

    // Return scheduled lessons first, then unscheduled lessons with new numbers
    return [...sortedScheduledLessons, ...numberedUnscheduledLessons];
  }, [pendingToBeScheduled, newSchedule]);

  // DEBUG: Track when displaySchedule changes
  useEffect(() => {
    console.log(
      '🔍 DEBUGGING - displaySchedule changed:',
      displaySchedule.map((l) => ({ id: l.id, number: l.number, date: l.newDateAndTime || 'NO DATE' }))
    );
  }, [displaySchedule]);

  // DEBUG: Track when activeAppointmentIndex changes
  useEffect(() => {
    console.log('🎯 DEBUGGING - activeAppointmentIndex changed to:', activeAppointmentIndex);
  }, [activeAppointmentIndex]);

  // Auto-update activeAppointmentIndex to point to first unscheduled lesson when displaySchedule changes
  useEffect(() => {
    if (displaySchedule.length > 0) {
      const firstUnscheduledIndex = displaySchedule.findIndex((lesson) => !lesson.newDateAndTime || lesson.newDateAndTime === 'NO DATE');

      console.log('🎯 AUTO-UPDATE - First unscheduled lesson found at index:', firstUnscheduledIndex);

      if (firstUnscheduledIndex >= 0 && firstUnscheduledIndex !== activeAppointmentIndex) {
        console.log('🎯 AUTO-UPDATE - Updating activeAppointmentIndex from', activeAppointmentIndex, 'to', firstUnscheduledIndex);
        setActiveAppointmentIndex(firstUnscheduledIndex);
      } else if (firstUnscheduledIndex === -1 && activeAppointmentIndex !== displaySchedule.length) {
        // All lessons are scheduled, set to beyond bounds
        console.log('🎯 AUTO-UPDATE - All lessons scheduled, setting to:', displaySchedule.length);
        setActiveAppointmentIndex(displaySchedule.length);
      }
    }
  }, [displaySchedule, activeAppointmentIndex]);

  // Helper function to reorder lessons chronologically and reassign lesson numbers
  const reorderLessonsByDate = (lessons) => {
    const lessonsWithDates = lessons.filter((lesson) => lesson.newDateAndTime);
    const lessonsWithoutDates = lessons.filter((lesson) => !lesson.newDateAndTime);

    // Sort lessons with dates by their timestamp
    const sortedLessonsWithDates = lessonsWithDates.sort((a, b) => new Date(a.newDateAndTime) - new Date(b.newDateAndTime));

    // Find the starting lesson number from the original pending lessons (not just selected ones)
    const startingLessonNumber = pendingToBeScheduled.length > 0 ? Math.min(...pendingToBeScheduled.map((lesson) => lesson.number)) : 1;

    // Reassign lesson numbers sequentially starting from the original starting number
    const reorderedLessons = sortedLessonsWithDates.map((lesson, index) => ({
      ...lesson,
      number: startingLessonNumber + index
    }));

    // Keep lessons without dates as-is (they'll get numbers assigned when dates are selected)
    return [...reorderedLessons, ...lessonsWithoutDates];
  };

  const handleSettingNewActiveLesson = (index) => {
    setActiveAppointmentIndex(index);
  };

  const blockSchedule = (timestamp) => {
    preBookDateAction(timestamp);
  };

  const unblockSchedule = (timestamp) => {
    removeFromPreBookAction(timestamp);
  };

  const handleAlternativeTimeSelection = (timestamp, coach) => {
    console.log('🟠 BOOKING MODAL - handleAlternativeTimeSelection called with:', {
      timestamp,
      coach,
      activeAppointmentIndex,
      newScheduleLength: newSchedule.length,
      pendingToBeScheduledLength: pendingToBeScheduled.length,
      displayScheduleLength: displaySchedule.length
    });

    // Prevent scheduling if all lessons are already scheduled
    if (newSchedule.length >= pendingToBeScheduled.length) {
      console.log('🟠 BOOKING MODAL - All lessons already scheduled, preventing new selection');
      return;
    }

    // Get the lesson from displaySchedule, fallback to pendingToBeScheduled if displaySchedule not ready
    const targetLesson = displaySchedule[activeAppointmentIndex] || pendingToBeScheduled[activeAppointmentIndex];

    console.log('🟠 BOOKING MODAL - Target lesson:', targetLesson);

    const newAppointment = {
      ...targetLesson,
      newDateAndTime: timestamp,
      alternativeCoach: coach // Add alternative coach information
    };

    console.log('🟠 BOOKING MODAL - New appointment created:', newAppointment);

    const updatedSchedule = [...newSchedule, newAppointment];
    const reorderedSchedule = reorderLessonsByDate(updatedSchedule);

    console.log('🟠 BOOKING MODAL - About to update state:', {
      updatedScheduleLength: updatedSchedule.length,
      reorderedScheduleLength: reorderedSchedule.length
    });

    setNewSchedule(reorderedSchedule);
    addToBookingSelectedTimestampAction(timestamp);
    blockSchedule(timestamp);
    calculateSessions(reorderedSchedule);

    console.log('🟠 BOOKING MODAL - State update complete');

    // DO NOT trigger bulk booking for alternative coach selections
    // Alternative coaches require individual booking logic
  };

  const handleDirectTimeSelection = (timestamp) => {
    console.log('🚦 DEBUGGING - handleDirectTimeSelection called with:', timestamp);
    console.log('🚦 DEBUGGING - activeAppointmentIndex:', activeAppointmentIndex);
    console.log('🚦 DEBUGGING - newSchedule.length:', newSchedule.length, 'pendingToBeScheduled.length:', pendingToBeScheduled.length);

    // Prevent scheduling if all lessons are already scheduled
    if (newSchedule.length >= pendingToBeScheduled.length) {
      console.log('🚦 DEBUGGING - All lessons already scheduled, preventing new selection');
      return;
    }

    // Get the lesson from displaySchedule, fallback to pendingToBeScheduled if displaySchedule not ready
    const targetLesson = displaySchedule[activeAppointmentIndex] || pendingToBeScheduled[activeAppointmentIndex];

    const newAppointment = {
      ...targetLesson,
      newDateAndTime: timestamp
    };

    const updatedSchedule = [...newSchedule, newAppointment];
    const reorderedSchedule = reorderLessonsByDate(updatedSchedule);
    setNewSchedule(reorderedSchedule);
    addToBookingSelectedTimestampAction(timestamp);
    blockSchedule(timestamp);
    calculateSessions(reorderedSchedule);

    // Auto-fill ALL remaining lessons if feature is enabled AND autoFillEnabled is true AND this is the first selection AND we're starting from scratch
    if (isBulkBookingEnabled && autoFillEnabled && activeAppointmentIndex === 0 && pendingToBeScheduled.length > 1 && updatedSchedule.length === 1) {
      const remainingLessons = pendingToBeScheduled.slice(1); // Skip first lesson as it's already selected

      // Get ALL available slots from ALL months, not just current month
      const allAvailableSlots = availableDatetimesMap ? Object.values(availableDatetimesMap).flat() : [...daysAvailableThisMonth];

      // Pass monthly quota information to the auto-scheduler
      const monthlyQuotas = availableSessionsThisMonth
        ? {
            currentMonth: {
              total: availableSessionsThisMonth.total || 0,
              used: (availableSessionsThisMonth.total || 0) - (availableSessionsThisMonth.availableRemaining || 0),
              available: availableSessionsThisMonth.availableRemaining || 0
            }
          }
        : null;

      // Auto-fill ALL remaining lessons with improved spacing and quota awareness
      const suggestions = autoFillSchedule(timestamp, remainingLessons, allAvailableSlots, monthlyQuotas, bookedTimestamps);

      if (suggestions.length > 0) {
        // Add bulk_booking=true to URL when auto-selection happens
        const currentUrl = new URL(window.location.href);
        const existingParams = Array.from(currentUrl.searchParams.entries());

        // Clear existing params and add bulk_booking first
        currentUrl.searchParams.delete('bulk_booking');
        existingParams.forEach(([key]) => {
          if (key !== 'bulk_booking') {
            currentUrl.searchParams.delete(key);
          }
        });

        // Add bulk_booking=true as first parameter
        currentUrl.searchParams.set('bulk_booking', 'true');

        // Re-add existing params after bulk_booking
        existingParams.forEach(([key, value]) => {
          if (key !== 'bulk_booking') {
            currentUrl.searchParams.set(key, value);
          }
        });

        // Update URL without page reload
        history.replace(currentUrl.pathname + currentUrl.search);

        const autoFilledSchedule = [...updatedSchedule];
        const newAlternativeSelections = new Set(alternativeSelections);

        // Process ALL suggestions to fill as many lessons as possible
        suggestions.forEach((suggestion, index) => {
          const lessonIndex = activeAppointmentIndex + 1 + index;
          if (lessonIndex < pendingToBeScheduled.length) {
            const autoAppointment = {
              ...pendingToBeScheduled[lessonIndex],
              newDateAndTime: suggestion.timestamp
            };
            autoFilledSchedule.push(autoAppointment);
            addToBookingSelectedTimestampAction(suggestion.timestamp);
            blockSchedule(suggestion.timestamp);

            if (suggestion.isAlternative) {
              newAlternativeSelections.add(suggestion.timestamp);
            }
          }
        });

        const reorderedAutoFilledSchedule = reorderLessonsByDate(autoFilledSchedule);
        setNewSchedule(reorderedAutoFilledSchedule);
        setAlternativeSelections(newAlternativeSelections);

        // Calculate sessions for ALL months that have new selections
        const monthsWithSelections = new Set();
        autoFilledSchedule.forEach((lesson) => {
          if (lesson.newDateAndTime) {
            monthsWithSelections.add(new Date(lesson.newDateAndTime));
          }
        });

        // Trigger calculateSessions for each month with selections
        monthsWithSelections.forEach((monthDate) => {
          calculateSessions(autoFilledSchedule, monthDate.toISOString());
        });

        // Move to next unfilled lesson if any remain, otherwise we're done
        const nextUnfilledIndex = autoFilledSchedule.length;
        if (nextUnfilledIndex < pendingToBeScheduled.length) {
          // There are still lessons that couldn't be auto-filled
          handleSettingNewActiveLesson(nextUnfilledIndex);
        } else {
          // All lessons have been auto-filled
          handleSettingNewActiveLesson(pendingToBeScheduled.length);
        }
      } else {
        // No suggestions found, move to manual selection
        const nextUnscheduledIndex = reorderedSchedule.length;
        console.log('📅 DEBUGGING - No auto-fill, moving to next unscheduled lesson at index:', nextUnscheduledIndex);
        if (nextUnscheduledIndex < pendingToBeScheduled.length) {
          handleSettingNewActiveLesson(nextUnscheduledIndex);
        } else {
          handleSettingNewActiveLesson(pendingToBeScheduled.length);
        }
      }
    } else {
      // Single lesson scheduling - move to next unscheduled lesson
      const nextUnscheduledIndex = reorderedSchedule.length;
      console.log('📅 DEBUGGING - Single lesson scheduled, moving to next unscheduled lesson at index:', nextUnscheduledIndex);
      if (nextUnscheduledIndex < pendingToBeScheduled.length) {
        handleSettingNewActiveLesson(nextUnscheduledIndex);
      } else {
        // All lessons are now scheduled - set index beyond bounds to disable further selection
        console.log('📅 DEBUGGING - All lessons scheduled, setting index to:', pendingToBeScheduled.length);
        handleSettingNewActiveLesson(pendingToBeScheduled.length);
      }
    }

    cleanSelectedTimestampAction();
  };

  const removeNewScheduleFromFinalList = (item) => {
    console.log('🗑️ Removing lesson:', item?.number, 'ID:', item?.id);
    console.log(
      '🗑️ DEBUGGING - newSchedule before:',
      newSchedule.map((l) => ({ id: l.id, number: l.number, date: l.newDateAndTime }))
    );
    console.log(
      '🗑️ DEBUGGING - pendingToBeScheduled before:',
      pendingToBeScheduled.map((l) => ({ id: l.id, number: l.number, date: l.newDateAndTime || 'NO DATE' }))
    );

    const filteredElement = newSchedule.find((lesson) => lesson?.id === item?.id);
    const filteredList = newSchedule.filter((lesson) => lesson?.id !== item?.id);
    console.log(
      '🗑️ Before reordering:',
      filteredList.map((l) => ({ id: l.id, number: l.number, date: l.newDateAndTime }))
    );
    const reorderedFilteredList = reorderLessonsByDate(filteredList);
    console.log(
      '🗑️ After reordering:',
      reorderedFilteredList.map((l) => ({ id: l.id, number: l.number, date: l.newDateAndTime }))
    );
    setNewSchedule(reorderedFilteredList);
    removeFromBookingSelectedTimestampAction(filteredElement.newDateAndTime);
    recalculateSessionsAfterExclusion(reorderedFilteredList, filteredElement?.newDateAndTime);
    unblockSchedule(filteredElement.newDateAndTime);

    // Remove from alternative selections if it was one
    if (alternativeSelections.has(filteredElement.newDateAndTime)) {
      const newAlternativeSelections = new Set(alternativeSelections);
      newAlternativeSelections.delete(filteredElement.newDateAndTime);
      setAlternativeSelections(newAlternativeSelections);
    }

    // Remove bulk_booking parameter if no lessons are scheduled
    if (reorderedFilteredList.length === 0) {
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.has('bulk_booking')) {
        currentUrl.searchParams.delete('bulk_booking');
        history.replace(currentUrl.pathname + currentUrl.search);
      }
    }

    // The activeAppointmentIndex will be updated by the useEffect when displaySchedule changes
    console.log('🎯 DEBUGGING - Scheduled lessons count:', reorderedFilteredList.length, 'Total lessons:', pendingToBeScheduled.length);

    cleanSelectedTimestampAction();
  };

  const redirect = (url) => {
    history.push(url);
  };

  const handleOneSessionOnlyToggle = () => {
    // Keep only the first lesson, remove all others
    if (newSchedule.length > 1) {
      const firstLesson = newSchedule[0];
      const lessonsToRemove = newSchedule.slice(1);

      // Remove bulk_booking parameter from URL when toggling to single session
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('bulk_booking');
      history.replace(currentUrl.pathname + currentUrl.search);

      // Get all months that had auto-filled sessions for cleanup
      const monthsToRecalculate = new Set();
      newSchedule.forEach((lesson) => {
        if (lesson.newDateAndTime) {
          monthsToRecalculate.add(new Date(lesson.newDateAndTime));
        }
      });

      // Remove from booking actions and unblock schedules
      lessonsToRemove.forEach((lesson) => {
        removeFromBookingSelectedTimestampAction(lesson.newDateAndTime);
        unblockSchedule(lesson.newDateAndTime);
      });

      setNewSchedule([firstLesson]);
      setAlternativeSelections(new Set());
      setAutoFillEnabled(false);

      // STEP 1: Use recalculateSessionsAfterExclusion for September (unblocks instead of blocks)
      if (firstLesson.newDateAndTime) {
        recalculateSessionsAfterExclusion([firstLesson], firstLesson.newDateAndTime);
      }

      // STEP 2: For cleared months - exclude the first lesson's month
      const clearedMonths = new Set();
      const firstLessonMonth = firstLesson.newDateAndTime ? firstLesson.newDateAndTime.substring(0, 7) : null;

      lessonsToRemove.forEach((lesson) => {
        if (lesson.newDateAndTime) {
          const monthStr = lesson.newDateAndTime.substring(0, 7); // YYYY-MM
          // DON'T include September if that's where the first lesson is
          if (monthStr !== firstLessonMonth) {
            clearedMonths.add(monthStr);
          }
        }
      });

      clearedMonths.forEach((monthStr) => {
        const monthTimestamp = `${monthStr}-01T10:00:00.000Z`;
        // Reset session count to (0/2) and unblock the month
        calculateSessions([], monthTimestamp);
        removeMonthFromPreBookAction(monthTimestamp);
      });

      // Navigate calendar to the next available month after the first lesson's month
      if (firstLesson.newDateAndTime) {
        const firstLessonDate = new Date(firstLesson.newDateAndTime);
        // Get next month for scheduling the second lesson
        const nextMonth = new Date(firstLessonDate.getFullYear(), firstLessonDate.getMonth() + 1, 1);
        console.log('📅 ONE SESSION - Navigating calendar from', firstLessonDate.toISOString(), 'to next month:', nextMonth.toISOString());
        setCurrentDateAction(nextMonth.toISOString());
      }

      handleSettingNewActiveLesson(1); // Move to next lesson for manual selection
    }
  };

  const shouldBlockMonth = (response) => {
    const canBook = response?.canBookThisMonth;
    return typeof canBook === 'boolean' && !canBook;
  };

  const transformIntoMap = (timestamps) => {
    const map = new Map();
    timestamps.forEach((ts) => {
      const key = buildMonthAndYearStr(ts);
      const prev = map.get(key) || [];
      map.set(key, [...prev, ts]);
    });
    return Object.fromEntries(map);
  };

  const calculateSessions = (booked, timestamp) => {
    const selectedTimestamps = transformIntoMap(booked.map(({ newDateAndTime }) => newDateAndTime));
    const selectedTimestamp = timestamp || calendar?.date;
    const response = calculateBookings({
      selectedTimestamp,
      selectedTimestamps,
      sessionsPerMonth
    });

    storeMonthSessionsAvailabilityAction({ timestamp: selectedTimestamp, response });
    if (shouldBlockMonth(response)) {
      preBookWholeMonthDateAction(selectedTimestamp);
    }
  };

  const recalculateSessionsAfterExclusion = (booked, timestamp) => {
    const selectedTimestamps = transformIntoMap(booked.map(({ newDateAndTime }) => newDateAndTime));
    const selectedTimestamp = timestamp || calendar?.date;
    const response = calculateBookings({
      selectedTimestamp,
      selectedTimestamps,
      sessionsPerMonth
    });

    storeMonthSessionsAvailabilityAction({ timestamp: selectedTimestamp, response });
    if (!shouldBlockMonth(response)) {
      removeMonthFromPreBookAction(selectedTimestamp);
    }
  };

  const executeAppointmentCreation = (appointmentData) => {
    if (redirectTo) {
      createAppointmentAndRedirectToAnotherPageAction({
        studentToken: student.token,
        data: appointmentData,
        successCallback: () => {
          endRecordingWholePage();
        },
        redirectFunction: redirect.bind(this, redirectTo)
      });
    } else {
      createAppointmentAndGetUpdatedStudentsBookingAction(student.token, selectedCourse?.id, appointmentData, () => {
        endRecordingWholePage();
      });
    }
  };

  const executeMultipleAppointmentCreations = async (appointmentsDataArray) => {
    // For multiple appointments, we need to handle loading state manually
    // and prevent modal from closing until all are done

    // Create a custom action that doesn't close the modal immediately
    try {
      // Dispatch loading state manually
      dispatch(cleanErrorState());
      dispatch(createAppointmentAndGetUpdatedStudentsBookingActions.createAppointmentAndUpdatedBooking());

      // Book appointments sequentially to avoid database transaction conflicts
      // The backend uses @dj_transaction.atomic which can cause locks with parallel requests
      const results = [];
      for (let i = 0; i < appointmentsDataArray.length; i++) {
        const appointmentData = appointmentsDataArray[i];
        try {
          await NewBookingAPI.createStudentBooking(student.token, appointmentData);
          console.log(`✅ Successfully booked appointment group ${i + 1} of ${appointmentsDataArray.length}`);
          results.push({ success: true, index: i });
        } catch (error) {
          console.error(`❌ Failed to book appointment group ${i + 1}:`, error);
          results.push({ success: false, index: i, error });
          // Continue trying remaining appointments even if one fails
        }
      }

      // Check if any failed
      const failures = results.filter((r) => !r.success);
      if (failures.length > 0) {
        alert(`Failed to book ${failures.length} appointment(s). Please check your bookings and try again for any missing sessions.`);
      }

      // Now update the UI and close the modal
      dispatch(getSessionsStatus(student.token, selectedCourse?.id));
      dispatch(setBulkingState(false));
      dispatch(setShowOptions(false));
      dispatch(setSelectedAction(''));
      dispatch(createAppointmentAndGetUpdatedStudentsBookingActions.createAppointmentAndUpdatedBookingSuccess());

      if (redirectTo) {
        redirect(redirectTo);
      }

      endRecordingWholePage();
    } catch (err) {
      sendSentryError({ err, context: 'executeMultipleAppointmentCreations' });
      const errorMessage = extractErrorMessage(err, 'Something went wrong while booking. Please, try again later.');
      dispatch(createAppointmentAndGetUpdatedStudentsBookingActions.createAppointmentAndUpdatedBookingFail(errorMessage));
    }
  };

  const handleAppointmentCreation = () => {
    const sorted = reorderLessonsByDate(newSchedule);

    // Group lessons by coach
    const lessonsByCoach = {};
    sorted.forEach((lesson) => {
      const coachId = lesson.alternativeCoach?.id || selectedTutor.id || matchedTutor.id;
      if (!lessonsByCoach[coachId]) {
        lessonsByCoach[coachId] = [];
      }
      lessonsByCoach[coachId].push(lesson);
    });

    const coachGroups = Object.entries(lessonsByCoach);

    if (coachGroups.length === 1) {
      // All lessons with same coach - use existing bulk booking logic
      const firstLessonToBeScheduled = sorted[0];
      const tutorId = firstLessonToBeScheduled.alternativeCoach?.id || selectedTutor.id || matchedTutor.id;
      const isAlternativeCoach = !!firstLessonToBeScheduled.alternativeCoach;

      const appointmentData = {
        auto_accept: student.is_staff,
        courseId: selectedCourse?.id,
        date: getDateFromIsoString(firstLessonToBeScheduled.newDateAndTime),
        duration: productType.duration,
        is_for_group_sessions: false,
        is_student_beta_tester: student.is_beta_tester,
        language: selectedCourse?.language.language,
        lesson_number: firstLessonToBeScheduled?.number,
        phone_number: '',
        product_type_id: productType.productTypeId,
        start_time: getTimeFromIsoString(firstLessonToBeScheduled.newDateAndTime),
        status: 'booked',
        student_comment: '',
        time_zone: timezone,
        tutor_id: tutorId,
        is_alternative_coach: isAlternativeCoach
      };

      const timestampsToSchedule = sorted
        .slice(1)
        .map(({ newDateAndTime }) => newDateAndTime)
        .filter((el) => el);

      if (timestampsToSchedule.length) {
        const datesAndTimes = timestampsToSchedule.map((timestamp) => {
          const date = getDateFromIsoString(timestamp);
          const startTime = getTimeFromIsoString(timestamp);
          return { date, start_time: startTime };
        });
        appointmentData.recurringReschedules = datesAndTimes;
        appointmentData.recurring_reschedule = true;
      }

      executeAppointmentCreation(appointmentData);
    } else {
      // Multiple coaches - need to book each group separately
      console.log('🎯 Multiple coaches detected, creating separate bookings for each coach group');

      const appointmentsToCreate = [];

      // Create appointment data for each coach group
      coachGroups.forEach(([coachId, lessons]) => {
        const coachName = lessons[0].alternativeCoach?.name || 'Primary Coach';
        console.log(`📚 Preparing ${lessons.length} lesson(s) with coach ${coachName} (ID: ${coachId})`);

        const firstLesson = lessons[0];
        const isAlternativeCoach = !!firstLesson.alternativeCoach;
        const appointmentData = {
          auto_accept: student.is_staff,
          courseId: selectedCourse?.id,
          date: getDateFromIsoString(firstLesson.newDateAndTime),
          duration: productType.duration,
          is_for_group_sessions: false,
          is_student_beta_tester: student.is_beta_tester,
          language: selectedCourse?.language.language,
          lesson_number: firstLesson?.number,
          phone_number: '',
          product_type_id: productType.productTypeId,
          start_time: getTimeFromIsoString(firstLesson.newDateAndTime),
          status: 'booked',
          student_comment: '',
          time_zone: timezone,
          tutor_id: coachId,
          is_alternative_coach: isAlternativeCoach
        };

        // If this coach group has multiple lessons, add them as recurring reschedules
        if (lessons.length > 1) {
          const additionalLessons = lessons.slice(1);
          const datesAndTimes = additionalLessons.map((lesson) => ({
            date: getDateFromIsoString(lesson.newDateAndTime),
            start_time: getTimeFromIsoString(lesson.newDateAndTime)
          }));
          appointmentData.recurringReschedules = datesAndTimes;
          appointmentData.recurring_reschedule = true;
        }

        appointmentsToCreate.push(appointmentData);
      });

      // Execute multiple appointment creations
      executeMultipleAppointmentCreations(appointmentsToCreate);
    }
  };

  const getNewAppointmentTime = (currentLessonId) => {
    const futureAppointment = newSchedule.find((l) => l?.id === currentLessonId);
    if (!futureAppointment) return;
    return futureAppointment.newDateAndTime;
  };

  const getSelectedTimestamps = useMemo(() => {
    return newSchedule.map(({ newDateAndTime }) => newDateAndTime);
  }, [newSchedule]);

  const Alert = <SessionsAlert availableSessionsPerMonth={availableSessionsThisMonth} monthName={getMonthTranslated(currentLang)[calendar?.month]} />;

  const isThisBookingDone = (lesson) => {
    return newSchedule.some((l) => l?.id === lesson?.id);
  };

  const isPrevBookingDone = (lesson) => {
    if (!lesson) return true;
    return newSchedule.some((l) => l?.id === lesson?.id);
  };

  return (
    <>
      <SessionsManagementModal
        disabledActionButton={!newSchedule.length}
        disabledCalendar={newSchedule.length >= pendingToBeScheduled.length}
        enableAlertOnLeaving
        futureSchedule={displaySchedule}
        handleExecution={handleAppointmentCreation}
        isExecuting={isSchedulingAppointment}
        isLoading={isLoadingFutureScheduleAvailability}
        modalTitle="Booking Live Practice"
        onCloseModal={() => {
          // Remove bulk_booking parameter when modal is closed
          const currentUrl = new URL(window.location.href);
          if (currentUrl.searchParams.has('bulk_booking')) {
            currentUrl.searchParams.delete('bulk_booking');
            history.replace(currentUrl.pathname + currentUrl.search);
          }
          closeBookingModalAction();
        }}
        onDirectTimeSelection={handleDirectTimeSelection}
        onAlternativeTimeSelection={handleAlternativeTimeSelection}
        type={CLASS_TYPES.tuition}
        productType={productType}
        renderAlert={() => Alert}
        renderAdditionalContent={() => (
          <OneSessionToggle visible={isBulkBookingEnabled && newSchedule.length > 1 && autoFillEnabled} onClick={handleOneSessionOnlyToggle} />
        )}
        renderAppointmentItem={(lesson, index) => {
          // Find if this lesson has been scheduled with an alternative coach
          const scheduledLesson = newSchedule.find((l) => l?.id === lesson?.id);
          const lessonWithCoach = scheduledLesson?.alternativeCoach ? { ...lesson, alternativeCoach: scheduledLesson.alternativeCoach } : lesson;

          return (
            <BookingSummaryItem
              key={`${lesson.id}-${lesson.number}-${lesson.newDateAndTime}`}
              active={index === activeAppointmentIndex}
              setNewActiveAppointment={handleSettingNewActiveLesson.bind(this, index)}
              disabled={!isThisBookingDone(lesson) && !isPrevBookingDone(displaySchedule[index - 1])}
              done={isThisBookingDone(lesson)}
              removeNewScheduleFromFinalList={removeNewScheduleFromFinalList}
              lesson={lessonWithCoach}
              finalTimestamp={getNewAppointmentTime(lesson?.id)}
              alternativeSelections={alternativeSelections}
              testId="schedule-session"
              timezone={timezone}
            />
          );
        }}
        selectedTimestamp={selectedTimestamp}
        selectedTimestamps={getSelectedTimestamps}
        testId="booking-tuition-session-modal"
      />
      <ErrorToast errorMsg={errorMessage} show={hasErrorOnBooking} onClose={cleanErrorStateAction} />
    </>
  );
};

const mapStateToProps = (state) => ({
  availableSessionsPerMonth: state.bookable.available.availableSessionsPerMonth,
  availableSessionsThisMonth: state.bookable.available.availableSessionsThisMonth,
  calendar: state.bookable.available.calendar,
  daysAvailableThisMonth: state.bookable.available.daysAvailableThisMonth,
  availableDatetimesMap: state.bookable.available.availableDatetimesMap,
  errorMessage: state.newBooking.bookingLessons.error,
  hasErrorOnBooking: state.newBooking.bookingLessons.hasErrorOnBooking,
  isLoadingFutureScheduleAvailability: state.bookable.futureSchedule.isLoadingFutureSchedule,
  isSchedulingAppointment: state.newBooking.bookingLessons.isCreatingAppointment,
  matchedTutor: state.selectedTutor.matchedTutor,
  pendingToBeScheduled: state.newBooking.bookingLessons.appointmentsToBeBooked,
  productType: selectProductType(state.orderInfo, CLASS_TYPES.tuition),
  selectedCourse: state.courses?.selectedCourseDetails?.courseDetails,
  selectedTutor: state.selectedTutor.selectedTutor,
  selectedTimestamp: state.bookable.available.calendar?.selectedTimestamp,
  sessionsPerMonth: state.bookable.available.sessionsPerMonth,
  student: state.user,
  timezone: state.time.timezone
});

BookingTuitionSessionModal.propTypes = {
  addToBookingSelectedTimestampAction: PropTypes.func.isRequired,
  availableSessionsThisMonth: PropTypes.object.isRequired,
  availableDatetimesMap: PropTypes.object.isRequired,
  calendar: PropTypes.object.isRequired,
  cleanErrorStateAction: PropTypes.func.isRequired,
  daysAvailableThisMonth: PropTypes.array.isRequired,
  cleanSelectedTimestampAction: PropTypes.func.isRequired,
  setCurrentDateAction: PropTypes.func.isRequired,
  createAppointmentAndGetUpdatedStudentsBookingAction: PropTypes.func.isRequired,
  createAppointmentAndRedirectToAnotherPageAction: PropTypes.func.isRequired,
  currentLang: PropTypes.string,
  errorMessage: PropTypes.string,
  hasErrorOnBooking: PropTypes.bool,
  isLoadingFutureScheduleAvailability: PropTypes.bool,
  isSchedulingAppointment: PropTypes.bool,
  matchedTutor: PropTypes.object,
  pendingToBeScheduled: PropTypes.array.isRequired,
  preBookDateAction: PropTypes.func.isRequired,
  preBookWholeMonthDateAction: PropTypes.func.isRequired,
  productType: PropTypes.object.isRequired,
  redirectTo: PropTypes.string,
  removeFromPreBookAction: PropTypes.func.isRequired,
  removeFromBookingSelectedTimestampAction: PropTypes.func.isRequired,
  selectedCourse: PropTypes.object.isRequired,
  selectedTutor: PropTypes.object,
  sessionsPerMonth: PropTypes.object,
  selectedTimestamp: PropTypes.string,
  storeMonthSessionsAvailabilityAction: PropTypes.func.isRequired,
  closeBookingModalAction: PropTypes.func.isRequired,
  student: PropTypes.object.isRequired,
  timezone: PropTypes.string.isRequired,
  removeMonthFromPreBookAction: PropTypes.func.isRequired
};

BookingTuitionSessionModal.defaultProps = {
  currentLang: 'en',
  isSchedulingAppointment: false,
  redirectTo: null
};

export default connect(mapStateToProps, {
  addToBookingSelectedTimestampAction: addToBookingSelectedTimestamp,
  createAppointmentAndGetUpdatedStudentsBookingAction: createAppointmentAndGetUpdatedStudentsBooking,
  createAppointmentAndRedirectToAnotherPageAction: createAppointmentAndRedirectToAnotherPage,
  cleanErrorStateAction: cleanErrorState,
  cleanSelectedTimestampAction: cleanSelectedTimestamp,
  setCurrentDateAction: setCurrentDate,
  preBookDateAction: preBookDate,
  preBookWholeMonthDateAction: preBookWholeMonthDate,
  removeFromPreBookAction: removeFromPreBook,
  removeFromBookingSelectedTimestampAction: removeFromBookingSelectedTimestamp,
  removeMonthFromPreBookAction: removeMonthFromPreBook,
  storeMonthSessionsAvailabilityAction: storeMonthSessionsAvailability,
  closeBookingModalAction: closeBookingModal
})(BookingTuitionSessionModal);
